import { create } from 'zustand';
import type { Unsubscribe } from '@firebase/firestore';
import {
  planEventsApi,
  rsvpToPlanEvent,
  type PlanEventInput,
  type RsvpRequest,
  type RsvpResult,
} from '../api/planEvents';
import type { PlanEvent, PlanEventRsvp } from '../types';
import { bundleIdsWithUpcomingEvents, upcomingEventsForBundle } from '../utils/eventCalendar';
import { getErrorMessage } from '../utils/errorHandler';
import { createPrefixedLogger } from '../utils/logger';
import { useAuthStore } from './authStore';

const log = createPrefixedLogger('planEventsStore');

// Module-level handles, the same pattern defensePlansStore uses for parties
// and assignments: one listener for the plan the owner's editor has open, one
// for the plan a viewer is looking at. The owner viewing their own plan opens
// both, a harmless duplicate. Each remembers its key so subscribe is a no-op
// when asked for the same plan again.
let ownUnsub: Unsubscribe | null = null;
let ownKey: string | null = null;
let viewedUnsub: Unsubscribe | null = null;
let viewedKey: string | null = null;
let rsvpUnsub: Unsubscribe | null = null;
let rsvpKey: string | null = null;

interface PlanEventsState {
  /** Events by plan id, as the listeners last delivered them (unsorted). */
  eventsByPlan: Record<string, PlanEvent[]>;
  /** Sign-ups by event id. Owner only; empty for anyone else. */
  rsvpsByEvent: Record<string, PlanEventRsvp[]>;
  /** False until the first snapshot of the sign-ups for the open event lands. */
  rsvpsLoaded: boolean;
  isSaving: boolean;
  error: string | null;

  subscribeOwnPlanEvents: (planId: string) => void;
  unsubscribeOwnPlanEvents: () => void;
  subscribeViewedPlanEvents: (planId: string) => void;
  unsubscribeViewedPlanEvents: () => void;
  subscribeRsvps: (eventId: string) => void;
  unsubscribeRsvps: () => void;

  /** Returns the new event's id, or null when it failed (see `error`). */
  createEvent: (planId: string, bundleId: string, input: PlanEventInput) => Promise<string | null>;
  updateEvent: (eventId: string, input: PlanEventInput) => Promise<boolean>;
  cancelEvent: (eventId: string) => Promise<boolean>;
  /** Cancels every upcoming event for a bundle. Returns how many it cancelled. */
  cancelUpcomingForBundle: (planId: string, bundleId: string) => Promise<number>;
  deleteEvent: (eventId: string) => Promise<boolean>;
  /**
   * Public sign-up (no account). Throws an RsvpError ({ kind, message }) on
   * failure. Touches no store state: the public page keeps its own form state.
   */
  rsvp: (request: RsvpRequest) => Promise<RsvpResult>;
  clearError: () => void;
  reset: () => void;
}

export const usePlanEventsStore = create<PlanEventsState>((set, get) => {
  const run = async <T>(label: string, fallback: T, task: () => Promise<T>): Promise<T> => {
    set({ isSaving: true, error: null });
    try {
      const result = await task();
      set({ isSaving: false });
      return result;
    } catch (error) {
      log.error(`${label} failed:`, error);
      set({ isSaving: false, error: getErrorMessage(error) });
      return fallback;
    }
  };

  const listen = (planId: string, role: 'own' | 'viewed') =>
    planEventsApi.subscribePlanEvents(
      planId,
      (events) => set((s) => ({ eventsByPlan: { ...s.eventsByPlan, [planId]: events } })),
      // A background listener failing is not the editor's business; the
      // panel just shows no events. permission-denied is warned in the api.
      (error) => log.warn(`${role} plan events listener failed:`, error)
    );

  return {
    eventsByPlan: {},
    rsvpsByEvent: {},
    rsvpsLoaded: false,
    isSaving: false,
    error: null,

    subscribeOwnPlanEvents: (planId) => {
      if (ownUnsub && ownKey === planId) return;
      ownUnsub?.();
      ownKey = planId;
      ownUnsub = listen(planId, 'own');
    },
    unsubscribeOwnPlanEvents: () => {
      ownUnsub?.();
      ownUnsub = null;
      ownKey = null;
    },
    subscribeViewedPlanEvents: (planId) => {
      if (viewedUnsub && viewedKey === planId) return;
      viewedUnsub?.();
      viewedKey = planId;
      viewedUnsub = listen(planId, 'viewed');
    },
    unsubscribeViewedPlanEvents: () => {
      viewedUnsub?.();
      viewedUnsub = null;
      viewedKey = null;
    },

    subscribeRsvps: (eventId) => {
      if (rsvpUnsub && rsvpKey === eventId) return;
      rsvpUnsub?.();
      rsvpKey = eventId;
      set({ rsvpsLoaded: false });
      rsvpUnsub = planEventsApi.subscribeRsvps(
        eventId,
        (rsvps) =>
          set((s) => ({
            rsvpsByEvent: { ...s.rsvpsByEvent, [eventId]: rsvps },
            rsvpsLoaded: true,
          })),
        (error) => {
          log.warn('rsvps listener failed:', error);
          set({ rsvpsLoaded: true });
        }
      );
    },
    unsubscribeRsvps: () => {
      rsvpUnsub?.();
      rsvpUnsub = null;
      rsvpKey = null;
      set({ rsvpsLoaded: false });
    },

    createEvent: async (planId, bundleId, input) => {
      const uid = useAuthStore.getState().user?.id;
      if (!uid) {
        set({ error: 'Sign in to schedule an event.' });
        return null;
      }
      return run('createEvent', null as string | null, async () => {
        const eventId = planEventsApi.newEventId();
        await planEventsApi.createEvent(eventId, uid, { planId, bundleId }, input);
        return eventId;
      });
    },

    updateEvent: (eventId, input) =>
      run('updateEvent', false, async () => {
        await planEventsApi.updateEvent(eventId, input);
        return true;
      }),

    cancelEvent: (eventId) =>
      run('cancelEvent', false, async () => {
        await planEventsApi.cancelEvent(eventId);
        return true;
      }),

    cancelUpcomingForBundle: async (planId, bundleId) => {
      const upcoming = upcomingEventsForBundle(get().eventsByPlan[planId] ?? [], planId, bundleId);
      if (upcoming.length === 0) return 0;
      return run('cancelUpcomingForBundle', 0, async () => {
        await Promise.all(upcoming.map((e) => planEventsApi.cancelEvent(e.id)));
        return upcoming.length;
      });
    },

    deleteEvent: (eventId) =>
      run('deleteEvent', false, async () => {
        await planEventsApi.deleteEvent(eventId);
        return true;
      }),

    rsvp: (request) => rsvpToPlanEvent(request),

    clearError: () => set({ error: null }),

    reset: () => {
      ownUnsub?.();
      ownUnsub = null;
      ownKey = null;
      viewedUnsub?.();
      viewedUnsub = null;
      viewedKey = null;
      rsvpUnsub?.();
      rsvpUnsub = null;
      rsvpKey = null;
      set({ eventsByPlan: {}, rsvpsByEvent: {}, rsvpsLoaded: false, isSaving: false, error: null });
    },
  };
});

/**
 * Bundle ids on this plan that have an upcoming scheduled event. The plan
 * store reads it so a bundle with an event is not pruned when its last
 * defense leaves. Empty until the events listener has delivered.
 */
export function bundleIdsKeptByEvents(planId: string): Set<string> {
  return bundleIdsWithUpcomingEvents(
    usePlanEventsStore.getState().eventsByPlan[planId] ?? [],
    planId
  );
}
